import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, SESSION_EXPIRED_EVENT } from "./api";
import { setSentryUser, clearSentryUser } from "./sentry";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null | object | false
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setSentryUser(data.id, data.email);
    } catch {
      setUser(false);
      clearSentryUser();
    } finally {
      setLoading(false);
    }
  }, []); // api/setUser/setLoading are stable; intentional one-shot fetcher

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onExpired = () => {
      setUser((u) => (u ? false : u));
      clearSentryUser();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const login = async (email, password) => {
    try {
      const { data, status } = await api.post("/auth/login", { email, password });
      // 202 = MFA challenge required. We don't set user yet; caller routes
      // the response to the challenge screen.
      if (status === 202 && data?.mfa_required) {
        return { ok: false, mfa_required: true, method: data.method, challenge_token: data.challenge_token };
      }
      setUser(data);
      setSentryUser(data.id, data.email);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const verifyMfa = async (challenge_token, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_token, code });
      setUser(data);
      setSentryUser(data.id, data.email);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const resendMfaEmail = async (challenge_token) => {
    try {
      await api.post("/auth/2fa/resend-email", { challenge_token });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (name, email, password, referralCode = null, acceptedLegal = false) => {
    try {
      const payload = { name, email, password };
      if (referralCode) payload.referral_code = referralCode;
      if (acceptedLegal) {
        payload.accept_terms = true;
        payload.accept_privacy = true;
      }
      // Carry the anonymous quiz's marketing-source tag (if any) into the
      // registration so we can attribute the eventual signup back to the
      // channel that originally drove the visit. Captured by the Quiz page
      // and stashed in sessionStorage.
      try {
        const quizRef = window.sessionStorage.getItem("quiz_ref");
        if (quizRef) payload.signup_ref = quizRef;
      } catch {
        // sessionStorage unavailable (private mode etc.) — skip silently.
      }
      // Visitor id from the page-view tracker — lets the backend join
      // the new user to their first-visit attribution chain.
      try {
        // Lazy require to avoid a circular import at module load.
        // eslint-disable-next-line global-require
        const { readVid } = require("./pageviewTracker");
        const vid = readVid();
        if (vid) payload.vid = vid;
      } catch {
        // tracker unavailable — registration still works without attribution
      }
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      setSentryUser(data.id, data.email);
      // Meta Pixel — successful registration is a mid-funnel
      // conversion. Lazy-imported to avoid bloating the auth bundle
      // and to no-op cleanly when REACT_APP_META_PIXEL_ID is unset.
      try {
        const { trackCompleteRegistration } = await import("./metaPixel");
        trackCompleteRegistration({ content_name: "Account Created" }, data.email);
      } catch {
        // pixel unavailable — registration still succeeds
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Even if the network call fails (e.g. session already expired), we
      // still want the client-side state cleared. No user-facing error needed.
    }
    setUser(false);
    clearSentryUser();
  };

  const value = React.useMemo(
    () => ({ user, loading, login, register, logout, refresh, verifyMfa, resendMfaEmail }),
    // login/register/logout are stable closures over setUser; refresh is memoized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
